import { useCallback, useMemo, useRef } from "react";
import { proxy, useSnapshot } from "valtio";
import { usePreviewContext } from "../../../contexts/preview-context";
import { Transform } from "../../../transform";
import { ISlideShow } from "../../slideshow/slideshow-js";
import { fixViewBox } from "../utils/fix-view-box";

/**
 * Because I couldn't create a function that converts the current slideshow transform to a viewBox,
 * I created a function to adjust the current viewBox and convert it to a new viewbox (that results in the same view)
 */
function createViewBox(
	slideShow: ISlideShow,
	viewBox: eXpress.math.IRect | undefined,
	adjustViewBox: (
		viewBox: eXpress.math.Rect,
		screenToPreviewScale: number,
	) => eXpress.math.Rect | undefined,
) {
	// Get the size of the active preview item
	const item = slideShow.items[slideShow.index];
	const itemInfo = slideShow.getItemInfo(item);
	if (!itemInfo) return undefined;

	const previewToScreenTransform = slideShow.getSlideShowToItemTransform(item);
	if (!previewToScreenTransform) return undefined;
	const previewToScreenScale = previewToScreenTransform.scale;

	const copiedViewBox = viewBox
		? eXpress.math.rect(viewBox.left, viewBox.top, viewBox.width, viewBox.height)
		: eXpress.math.rect(itemInfo.size); // Full preview

	const adjustedViewBox = adjustViewBox(copiedViewBox, previewToScreenScale);
	if (!adjustedViewBox) return undefined;

	// Adjust size of the viewBox
	const placeInRect = eXpress.math
		.rect(slideShow.portalSize)
		.shrink(slideShow.portalPaddings)
		.shrink(slideShow.viewBoxMargins);

	const portalRectViewBox = eXpress.math.rect(placeInRect).fill(adjustedViewBox); // ViewBox for everything in the portalRect

	return portalRectViewBox;
}

function getSlideShowActivePreviewItemToScreenTransform(
	slideShow: ISlideShow,
): Transform | undefined {
	// Get the size of the active preview item
	const item = slideShow.items[slideShow.index];
	const itemInfo = slideShow.getItemInfo(item);
	if (!itemInfo) return undefined;

	// Get the transformation from preview item to screen coordinates
	const previewToScreenTransform = slideShow.getSlideShowToItemTransform(item);
	return previewToScreenTransform;
}

/** Shared state */
const previewPanPinchState = proxy({
	isPreviewPanning: false,
	isPreviewPinching: false,
});

export function usePreviewPinching() {
	/** An object containing information of preview zooming */
	const previewPinchRef = useRef<
		| {
				/** Hold spread we started pinching with */
				startSpread: eXpress.core.Spread;

				// The distance between the the fingers at start
				startPinchDistance?: number;

				// The center of the pinch-zooming (in viewbox coordinates)
				startPinchCenter?: eXpress.math.Point;

				// The transformation from preview item to screen (used to convert touch screen coordinates to preview)
				screenToPreviewTransform?: Transform;

				// The viewBox at start (also remember and use to viewBoxMargins?)
				startViewBox: Readonly<eXpress.math.IRect> | undefined;
		  }
		| undefined
	>(undefined);

	const { calcViewBoxInfo, slideShowPreviewRef } = usePreviewContext();

	const { isPreviewPinching } = useSnapshot(previewPanPinchState);

	const start = useCallback(
		function startPreviewPinching(
			selectedSpread: eXpress.core.Spread,
			pinchDistance: number,
			pinchOrigin: [x: number, y: number],
		) {
			const slideShow = slideShowPreviewRef.current?.slideShow;
			if (!slideShow) return;

			const previewToScreenTransform =
				getSlideShowActivePreviewItemToScreenTransform(slideShow);
			if (!previewToScreenTransform) return;

			const [originScreenX, originScreenY] = pinchOrigin;
			const screenPoint = new eXpress.math.Point(originScreenX, originScreenY);
			const screenToPreviewTransform = previewToScreenTransform.inverse();
			const startPinchCenter = screenToPreviewTransform.transformPoint(screenPoint);

			previewPanPinchState.isPreviewPinching = true;
			previewPinchRef.current = {
				startSpread: selectedSpread,
				startPinchDistance: pinchDistance,
				startPinchCenter,
				screenToPreviewTransform,
				startViewBox: slideShow.viewBox,
			};
		},
		[slideShowPreviewRef],
	);

	const update = useCallback(
		function previewPinching(pinchDistance: number, pinchOrigin: [x: number, y: number]) {
			if (!previewPinchRef.current) return;

			const {
				startSpread,
				startPinchDistance,
				startPinchCenter,
				screenToPreviewTransform,
				startViewBox,
			} = previewPinchRef.current;

			if (startPinchDistance && startPinchCenter && screenToPreviewTransform) {
				const scaleFactor = 1 / Math.max(0.01, pinchDistance / startPinchDistance); // prevent negative values

				const slideShow = slideShowPreviewRef.current?.slideShow;
				if (!slideShow) return;

				const { viewBox: fullViewBox } = calcViewBoxInfo(startSpread, undefined);
				if (!fullViewBox) return;

				const [originScreenX, originScreenY] = pinchOrigin;
				const screenPoint = new eXpress.math.Point(originScreenX, originScreenY);
				const previewPanning = screenToPreviewTransform
					.transformPoint(screenPoint)
					.to(startPinchCenter)
					.scale(scaleFactor);

				const viewBox = createViewBox(slideShow, startViewBox, (newViewBox) =>
					fixViewBox(
						newViewBox
							.scale(scaleFactor, scaleFactor, startPinchCenter)
							.offset(previewPanning),
						fullViewBox,
					),
				);

				slideShow.updateSlideShow(() => {
					slideShow.viewBox = viewBox;
				});
			}
		},
		[calcViewBoxInfo, slideShowPreviewRef],
	);

	const end = useCallback(() => {
		previewPinchRef.current = undefined;
		previewPanPinchState.isPreviewPinching = false;
	}, []);

	const cancel = useCallback(() => {
		previewPinchRef.current = undefined;
		previewPanPinchState.isPreviewPinching = false;
	}, []);

	return [
		isPreviewPinching,
		useMemo(
			() => ({
				start,
				/** Pinch + move */
				update,
				end,
				cancel,
			}),
			[start, update, end, cancel],
		),
	] as const;
}

export function usePreviewPanning() {
	/** An object containing information of preview panning/zooming */
	const previewPanZoomRef = useRef<
		| {
				/** Hold spread we started panning with */
				startSpread: eXpress.core.Spread;

				// The viewBox at start (also remember and use to viewBoxMargins?)
				startViewBox: Readonly<eXpress.math.IRect> | undefined;
		  }
		| undefined
	>(undefined);

	const { calcViewBoxInfo, slideShowPreviewRef } = usePreviewContext();

	const { isPreviewPanning } = useSnapshot(previewPanPinchState);

	const start = useCallback(
		function startPreviewPanning(selectedSpread: eXpress.core.Spread) {
			const slideShow = slideShowPreviewRef.current?.slideShow;
			if (!slideShow) return;

			previewPanPinchState.isPreviewPanning = true;
			previewPanZoomRef.current = {
				startSpread: selectedSpread,
				startViewBox: slideShow.viewBox,
			};
		},
		[slideShowPreviewRef],
	);

	const update = useCallback(
		function movePreviewPanning(dx: number, dy: number) {
			if (!previewPanZoomRef.current) return;
			const slideShow = slideShowPreviewRef.current?.slideShow;
			if (!slideShow) return;

			const { startSpread, startViewBox } = previewPanZoomRef.current;

			const { viewBox: fullViewBox } = calcViewBoxInfo(startSpread, undefined);
			if (!fullViewBox) return;

			// Calculate new requested transforms
			const viewBox = createViewBox(
				slideShow,
				startViewBox,
				(newViewBox, screenToPreviewScale) =>
					fixViewBox(
						newViewBox.offset(-dx / screenToPreviewScale, -dy / screenToPreviewScale),
						fullViewBox,
					),
			);

			slideShow.updateSlideShow(() => {
				slideShow.viewBox = viewBox;
			});
		},
		[calcViewBoxInfo, slideShowPreviewRef],
	);

	const end = useCallback(
		function endPreviewPanning(dx: number, dy: number) {
			// Remark: Do should we still need update position? can the end be a different position as the last move?
			update(dx, dy);
			previewPanZoomRef.current = undefined;
			previewPanPinchState.isPreviewPanning = false;
		},
		[update],
	);

	const cancel = useCallback(function cancelPreviewPanning() {
		previewPanZoomRef.current = undefined;
		previewPanPinchState.isPreviewPanning = false;
	}, []);

	return [
		isPreviewPanning,
		useMemo(
			() => ({
				start,
				update,
				end,
				cancel,
			}),
			[cancel, end, start, update],
		),
	] as const;
}
